import { useCallback, useState } from "react";
import { useRequestProposalReReviewMutation } from "work/proposals/negotiation/proposal-collaboration-api";
import { buildRequestProposalReviewCommand } from "work/proposals/negotiation/proposal-command-builder";
import type { ProposalCollaborationEnvelope } from "work/proposals/negotiation/proposal-collaboration-contract";
import type ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";

/**
 * Requesting a re-review from one reviewer, wired for the reviewer popover's per-row button.
 *
 * It fires immediately rather than staging behind the popover's Save, which is a deliberate
 * departure from how every other control in that popover behaves. Those controls edit the roster,
 * which is proposal state the leader composes and then commits; a re-review request is not
 * proposal content at all — it dismisses one reviewer's approval and asks them to look again — so
 * there is nothing to compose alongside it.
 *
 * The request must be a command against the negotiation stream, never a local edit: the envelope's
 * `reviewerApprovals` is projected from that stream, and the background parity repair rebuilds the
 * owned reviewer row from it, so a client-side approval reset would be both invisible and
 * short-lived.
 */
export function useRequestProposalReReview(params: {
  envelope?: ProposalCollaborationEnvelope;
  viewerScope?: string;
  canManageReviewers: boolean;
}) {
  const { envelope, viewerScope, canManageReviewers } = params;
  const [requestProposalReReview] = useRequestProposalReReviewMutation();
  const [pendingUserIds, setPendingUserIds] = useState<ReadonlySet<string>>(EmptyPendingUserIds);

  // A legacy proposal has no negotiation stream to record the request in, so the button stays
  // hidden rather than offering an action the API would reject.
  const canRequestReReview = canManageReviewers && Boolean(envelope) && Boolean(viewerScope);

  const requestReReview = useCallback(
    (reviewer: ProposalReviewer) => {
      if (!envelope || !viewerScope) return;
      const reviewerUserId = reviewer.userId.value;

      setPendingUserIds((current) => withUserId(current, reviewerUserId));
      void runReReviewRequest({
        request: requestProposalReReview,
        envelope,
        viewerScope,
        reviewerUserId,
        reviewerLegalEntityId: reviewer.entityMember.legalEntityId.value,
        onSettled: () => setPendingUserIds((current) => withoutUserId(current, reviewerUserId)),
      });
    },
    [envelope, requestProposalReReview, viewerScope],
  );

  return { canRequestReReview, pendingUserIds, requestReReview };
}

const EmptyPendingUserIds: ReadonlySet<string> = new Set<string>();

function withUserId(current: ReadonlySet<string>, userId: string): ReadonlySet<string> {
  const next = new Set(current);
  next.add(userId);
  return next;
}

function withoutUserId(current: ReadonlySet<string>, userId: string): ReadonlySet<string> {
  const next = new Set(current);
  next.delete(userId);
  return next;
}

/**
 * Module scope on purpose: `try`/`finally` inside a component forces a React Compiler bailout for
 * the whole component (see app/CLAUDE.md), and this routine genuinely needs the `finally` so a
 * rejected request still clears its pending badge.
 */
async function runReReviewRequest(params: {
  request: ReturnType<typeof useRequestProposalReReviewMutation>[0];
  envelope: ProposalCollaborationEnvelope;
  viewerScope: string;
  reviewerUserId: string;
  reviewerLegalEntityId: string;
  onSettled: () => void;
}): Promise<void> {
  try {
    await params
      .request({
        negotiationId: params.envelope.negotiationId,
        viewerScope: params.viewerScope,
        command: buildRequestProposalReviewCommand({
          envelope: params.envelope,
          reviewerUserId: params.reviewerUserId,
          reviewerLegalEntityId: params.reviewerLegalEntityId,
        }),
      })
      .unwrap();
  } catch {
    // The endpoint's own onQueryStarted has already rebased Redux from a 409 envelope, and the
    // collaboration tag invalidation refetches on anything else. Surfacing a second error here
    // would double-report what the page already shows.
  } finally {
    params.onSettled();
  }
}
